import { useFormik } from 'formik';
import { Button, FormHelperText, Input } from '@selfinspectionorg/ui-kit';
import OTPInput from 'react18-input-otp';
import { useRouter } from '@tanstack/react-router';
import { SetNewPasswordForm } from '../new-password';
import styles from './ForgotPassword.module.scss';
import { useResetPassword } from './useResetPassword';
import { emailValidationSchema } from './form/emailValidation.schema';
import { codeValidationSchema } from './form/codeValidation.schema';
import { Card } from '@/components/ui/Card';

export const ForgotPassword = () => {
  const {
    sendRecoveryCode,
    confirmCode,
    resendCode,
    reset,
    isCodeSent,
    isCodeConfirmed,
    email,
    code,
    resendCountdown,
    isSendingRecoveryCode,
    isConfirmingRecoveryCode,
    sendRecoveryCodeError,
    confirmRecoveryCodeError,
  } = useResetPassword();

  const emailForm = useFormik({
    initialValues: { email: '' },
    validationSchema: emailValidationSchema,
    validateOnBlur: false,
    onSubmit: (values) => sendRecoveryCode(values.email),
  });

  const codeForm = useFormik({
    initialValues: { code: '' },
    validationSchema: codeValidationSchema,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: (values) => confirmCode(values.code),
  });

  const router = useRouter();

  // Once the code is confirmed the wizard swaps to the set-new-password step, which owns its own Card
  // (so it replaces this one rather than nesting).
  if (isCodeConfirmed) {
    return <SetNewPasswordForm email={email} code={code} />;
  }

  let content = null;

  if (!isCodeSent) {
    content = (
      <>
        <Card.Title title="Forgot Password?" description="We will send password reset information to your email" />
        <div>
          <Input
            label="Email"
            name="email"
            type="email"
            placeholder="example@gmail.com"
            required
            value={emailForm.values.email}
            onChange={emailForm.handleChange}
            errored={!!emailForm.errors.email || !!sendRecoveryCodeError}
            helperMessage={emailForm.errors.email || sendRecoveryCodeError || undefined}
            testId="email-forgot-password"
          />
          <Button
            width="100%"
            onClick={emailForm.submitForm}
            disabled={emailForm.isSubmitting}
            isLoading={isSendingRecoveryCode}
            data-testid="reset-password-button"
          >
            Reset password
          </Button>
        </div>
      </>
    );
  } else {
    content = (
      <>
        <Card.Title
          title="Enter verification code"
          description="Check your email, we have sent you a verification code to reset your password"
        />
        <div>
          <div className={styles.codeInputWrapper}>
            <OTPInput
              value={codeForm.values.code}
              onChange={codeForm.handleChange('code')}
              numInputs={6}
              containerStyle={styles.codeInputContainer}
              inputStyle={styles.codeInput}
              isInputNum
              shouldAutoFocus
              isDisabled={isConfirmingRecoveryCode}
              data-testid="verification-code-input"
            />
          </div>
          <Button
            width="100%"
            onClick={codeForm.submitForm}
            disabled={codeForm.isSubmitting || !codeForm.isValid}
            isLoading={isConfirmingRecoveryCode}
            data-testid="confirm-code-button"
          >
            Confirm
          </Button>
          {confirmRecoveryCodeError && <FormHelperText errored>{confirmRecoveryCodeError}</FormHelperText>}
          <div className={styles.resendWrapper}>
            <span className={styles.resendText}>Didn't receive the code?</span>
            <button
              className={styles.resendButton}
              onClick={resendCode}
              disabled={isSendingRecoveryCode || resendCountdown > 0}
              data-testid="resend-code-button"
            >
              {resendCountdown > 0 ? `Resend code (${resendCountdown}s)` : 'Resend code'}
            </button>
          </div>
        </div>
      </>
    );
  }

  const handleBackClick = () => {
    if (isCodeSent) {
      reset();
    } else {
      router.history.back();
    }
  };

  return (
    <Card className={styles.card}>
      <Card.BackButton onClick={handleBackClick} testId="back-button" />
      {content}
    </Card>
  );
};
